import { memo } from 'react';

import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import { WORKSPACE_AGENT_PANEL_PERSON_AVATAR } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';

export type WorkspaceAgentPersonChipProps = {
  avatarUrl?: string;
  'data-testid'?: string;
  name: string;
};

/**
 * A person named inside running copy — sits inline in a bullet rather than standing alone, so it
 * inherits the surrounding type size instead of setting its own.
 *
 * The root is `inline-block` with `max-w-full` so long names truncate without `inline-flex`, which
 * would lift the label off the prose baseline. The avatar keeps its own fixed-height slot; the name
 * truncates in a sibling `inline-block` span with a native `title` for the full string.
 *
 * The avatar alone gets a fixed-height slot so it centres against the line without an `inline-block`
 * image stretching text selection to the full line height. One pixel of lift closes the gap between
 * that geometric centre and where cap-height text actually sits.
 */
export const WorkspaceAgentPersonChip = memo(
  ({ avatarUrl, 'data-testid': testId, name }: WorkspaceAgentPersonChipProps) => (
    // Nowrap so the avatar can never be left behind at the end of a line.
    <span
      className="inline-block max-w-full whitespace-nowrap align-baseline font-medium ads:text-primary text-gray-12"
      data-testid={testId}
    >
      {avatarUrl ? (
        // Decorative: the name sits right beside it, so alt text would just be read twice.
        <span className="relative -top-px mr-1 inline-flex h-5 w-3.5 shrink-0 items-center align-middle leading-none">
          <WorkspaceAgentPanelImage
            {...WORKSPACE_AGENT_PANEL_PERSON_AVATAR}
            alt=""
            className="size-3.5 rounded-full object-cover"
            src={avatarUrl}
          />
        </span>
      ) : null}
      <span
        className={cn('inline-block truncate align-baseline', avatarUrl ? 'max-w-[calc(100%-1.125rem)]' : 'max-w-full')}
        title={name}
      >
        {name}
      </span>
    </span>
  ),
);

WorkspaceAgentPersonChip.displayName = 'WorkspaceAgentPersonChip';
